import { useState } from "react";
import { Button } from "../../components/Button";
import { ApiError, api } from "../../lib/api";
import type { ReportCsvColumn, ReportFilters } from "../../lib/api";
import { saveFile } from "../../lib/saveFile";
import { formatInZone } from "../../lib/storeTime";
import styles from "./ReportsPanel.module.css";

// Table order; the file's columns follow it whatever order they were ticked in.
const CSV_COLUMNS: readonly { key: ReportCsvColumn; label: string; byDefault: boolean }[] = [
  { key: "camera", label: "Camera name", byDefault: true },
  { key: "location", label: "Camera location", byDefault: true },
  { key: "start", label: "Start time", byDefault: true },
  { key: "end", label: "End time", byDefault: true },
  { key: "duration", label: "Duration", byDefault: false },
  { key: "severity", label: "Severity", byDefault: true },
  { key: "category", label: "Category", byDefault: true },
  { key: "description", label: "Description", byDefault: true },
  { key: "reason", label: "Reason", byDefault: false },
  { key: "alert", label: "Alert (sent / suppressed)", byDefault: true },
  { key: "link", label: "Link", byDefault: true },
];

const COLUMNS_KEY = "shobdo.reportCsvColumns";

const savedColumns = (): Set<ReportCsvColumn> => {
  const known = new Set(CSV_COLUMNS.map((c) => c.key));
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? "null");
    if (Array.isArray(saved)) return new Set(saved.filter((k) => known.has(k)));
  } catch {
    // unreadable choice: fall back to the defaults
  }
  return new Set(CSV_COLUMNS.filter((c) => c.byDefault).map((c) => c.key));
};

type ReportCsvDialogProps = {
  storeId: string;
  filters: ReportFilters;
  timeZone: string;
  rowCount: number | null;
  onClose: () => void;
};

export const ReportCsvDialog = ({
  storeId,
  filters,
  timeZone,
  rowCount,
  onClose,
}: ReportCsvDialogProps) => {
  const [chosen, setChosen] = useState(savedColumns);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = (key: ReportCsvColumn) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      localStorage.setItem(COLUMNS_KEY, JSON.stringify([...next]));
      return next;
    });

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const columns = CSV_COLUMNS.filter((c) => chosen.has(c.key)).map((c) => c.key);
      const file = await api.downloadStoreReportCsv(storeId, filters, columns);
      saveFile(file.blob, file.filename);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't download the report.");
    } finally {
      setBusy(false);
    }
  };

  const rows = rowCount === null ? "" : `${rowCount} row${rowCount === 1 ? "" : "s"} · `;
  const span = `${formatInZone(filters.from, timeZone)} – ${formatInZone(filters.to, timeZone)}`;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the Cancel button closes it from the keyboard
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <dialog open className={styles.dialog} aria-label="Download CSV">
        <h2 className={styles.dialogTitle}>Download CSV</h2>
        <p className={styles.dialogSub}>
          {rows}
          {span} ({timeZone}) · current filters
        </p>
        <fieldset className={styles.columns}>
          <legend>Columns</legend>
          {CSV_COLUMNS.map((c) => (
            <label key={c.key} className={styles.check}>
              <input type="checkbox" checked={chosen.has(c.key)} onChange={() => toggle(c.key)} />
              {c.label}
            </label>
          ))}
        </fieldset>
        <p className={styles.dialogNote}>Your column choice is remembered on this device.</p>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <div className={styles.dialogActions}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy || chosen.size === 0} onClick={() => void download()}>
            Download
          </Button>
        </div>
      </dialog>
    </div>
  );
};
